import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { useAppSelector } from 'store/hooks';
import { Stack } from 'components';

import { NoteForm } from '../NoteForm';
import { useNoteFormContext } from '../context';
import { useNoteFormActions } from '../hooks';

type FinalNoteFormProps = {
  clientId: string;
  note?: ClientProfileContentItem;
  disabled?: boolean;
  onCancel?: () => void;
  onSubmitSuccess?: () => void;
};

const FinalNoteFormContent = ({
  onCancel,
  onSubmitSuccess
}: {
  onCancel?: () => void;
  onSubmitSuccess?: () => void;
}) => {
  const { isManagerReview, disabled } = useNoteFormContext();
  const { handleSubmit } = useNoteFormActions({ isPopup: true, onSubmitSuccess });
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);

  return (
    <NoteForm.Form onSubmit={handleSubmit}>
      <NoteForm.AiScribe.Status />

      <NoteForm.FieldsContainer expanded>
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.NoteType disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.DateOfEncounter disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>

        {!isManagerReview && (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        )}

        {isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.TimeSpent disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.Manager disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.Chw disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.Provider
                  disabled={disabled}
                  required={Boolean(isBillingEnabled)}
                />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
          </>
        )}

        {!isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.NextContact disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.FullWidth>
              <NoteForm.Fields.TypeOfSupport disabled={disabled} />
            </NoteForm.FieldsContainer.FullWidth>
          </>
        )}
      </NoteForm.FieldsContainer>

      <NoteForm.AiScribe.AudioPlayer />

      <NoteForm.FieldsContainer.HalfRow>
        <NoteForm.Fields.Note disabled={disabled} />
      </NoteForm.FieldsContainer.HalfRow>
      <NoteForm.FieldsContainer.HalfRow>
        <NoteForm.Fields.DocumentationTime disabled={disabled} />
      </NoteForm.FieldsContainer.HalfRow>

      <NoteForm.Actions>
        <div></div>
        <NoteForm.Actions.Right>
          <Stack display="flex" gap="12px" flexDirection="row" alignItems="center">
            <Stack width="124px">
              <NoteForm.CancelButton onClick={onCancel}>Cancel</NoteForm.CancelButton>
            </Stack>
            <Stack width="124px">
              <NoteForm.SubmitButton>Final</NoteForm.SubmitButton>
            </Stack>
          </Stack>
        </NoteForm.Actions.Right>
      </NoteForm.Actions>
    </NoteForm.Form>
  );
};

/**
 * Preset component for editing finalized notes.
 * Used in newNoteView when editing a final note.
 * Has Cancel and Final buttons (no delete or edit).
 * No autosave since this is for finalized notes.
 */
export const FinalNoteForm = ({
  clientId,
  note,
  disabled = false,
  onCancel,
  onSubmitSuccess
}: FinalNoteFormProps) => {
  return (
    <NoteForm.Root clientId={clientId} note={note} disabled={disabled}>
      <FinalNoteFormContent onCancel={onCancel} onSubmitSuccess={onSubmitSuccess} />
    </NoteForm.Root>
  );
};
